/**
 * Live model: unit states and their colours, freshness, filters, realtime frame merging and
 * track colouring. Pure functions (tested in model.test.ts).
 */
import type { LucideIcon } from 'lucide-react';
import { CircleParking, Cog, Navigation, Pause, Square, WifiOff } from 'lucide-react';
import type { components } from '@/lib/api/schema';
import { msg } from '@/lib/i18n';
import type { Tone } from '@/ui';
import type { GeoLine, GeoTone, LngLat } from '@/ui/Map/GeoMap';

type S = components['schemas'];
export type LiveUnit = S['LiveUnit'];
export type LiveUnitsOut = S['LiveUnitsOut'];
export type LiveUnitDetail = S['LiveUnitDetail'];
export type TrackOut = S['TrackOut'];
export type TimelineOut = S['TimelineOut'];

export type UnitState = 'driving' | 'idling' | 'stopped' | 'pto' | 'parked' | 'offline';

export const STATES: Record<UnitState, { label: string; pill: Tone; geo: GeoTone; icon: LucideIcon }> = {
  driving: { label: msg('Driving'), pill: 'good', geo: 'good', icon: Navigation },
  idling: { label: msg('Idling'), pill: 'medium', geo: 'medium', icon: Pause },
  pto: { label: msg('PTO'), pill: 'accent', geo: 'accent', icon: Cog },
  stopped: { label: msg('Stopped'), pill: 'caution', geo: 'caution', icon: Square },
  parked: { label: msg('Parked'), pill: 'info', geo: 'primary', icon: CircleParking },
  offline: { label: msg('Offline'), pill: 'neutral', geo: 'neutral', icon: WifiOff },
};

/** The filter chips, in this order ('stopped' only when a unit is in it). */
export const STATE_ORDER: UnitState[] = ['driving', 'idling', 'pto', 'stopped', 'parked', 'offline'];

export function stateOf(u: { state: string }): UnitState {
  return (u.state in STATES ? u.state : 'offline') as UnitState;
}

// ---------------------------------------------------------------- freshness

export type Freshness = 'live' | 'recent' | 'stale' | 'none';

/** How old the last message is: live < 2 min, recent < 15 min, stale beyond, none = never. */
export function freshness(ts: string | null | undefined, now: number): { level: Freshness; ageS: number | null } {
  if (!ts) return { level: 'none', ageS: null };
  const ageS = Math.max(0, (now - new Date(ts).getTime()) / 1000);
  return { level: ageS < 120 ? 'live' : ageS < 900 ? 'recent' : 'stale', ageS };
}

/** The server marks a unit offline after 5 minutes without data (telemetry mark_offline job). */
export const OFFLINE_AFTER_S = 300;

/**
 * The list is loaded once and then only moved by frames, and a unit that falls silent sends no
 * frame: show it offline here once its last message is older than the server's gap.
 */
export function withSilentOffline(units: LiveUnit[], now: number): LiveUnit[] {
  let changed = false;
  const out = units.map((u) => {
    if (u.state === 'offline' || !u.ts || now - new Date(u.ts).getTime() < OFFLINE_AFTER_S * 1000) return u;
    changed = true;
    return { ...u, state: 'offline', state_since: u.ts };
  });
  return changed ? out : units;
}

// ---------------------------------------------------------------- filters

export function unitMatches(u: LiveUnit, q: string | undefined, extra?: string | null): boolean {
  if (!q) return true;
  const needle = q.trim().toLowerCase();
  return [u.name, u.plate, u.driver?.name, extra].some((v) => !!v && v.toLowerCase().includes(needle));
}

export function filterUnits(units: LiveUnit[], states: UnitState[], q: string | undefined, placeOf?: (id: string) => string | null): LiveUnit[] {
  const wanted = new Set(states);
  return units.filter((u) => (!wanted.size || wanted.has(stateOf(u))) && unitMatches(u, q, placeOf?.(u.id)));
}

export function countStates(units: LiveUnit[]): Record<UnitState, number> {
  const out = { driving: 0, idling: 0, stopped: 0, pto: 0, parked: 0, offline: 0 };
  for (const u of units) out[stateOf(u)] += 1;
  return out;
}

// ---------------------------------------------------------------- realtime frames

/** One unit in a `live.<project>` frame (telemetry/stream.py `flush_live`); times in epoch seconds. */
export interface LiveFrameUnit {
  unit_type: 'vehicle' | 'asset';
  unit_id: string;
  ts: number;
  lat?: number | null;
  lon?: number | null;
  speed?: number | null;
  heading?: number | null;
  state: string;
  state_since?: number | null;
  ignition?: boolean | null;
}

export function parseFrame(data: unknown): LiveFrameUnit[] {
  const units = (data as { units?: unknown } | null)?.units;
  if (!Array.isArray(units)) return [];
  return units.filter(
    (u): u is LiveFrameUnit =>
      !!u &&
      typeof u === 'object' &&
      ((u as LiveFrameUnit).unit_type === 'vehicle' || (u as LiveFrameUnit).unit_type === 'asset') &&
      typeof (u as LiveFrameUnit).unit_id === 'string' &&
      typeof (u as LiveFrameUnit).ts === 'number',
  );
}

const iso = (epoch: number | null | undefined) => (epoch == null ? null : new Date(epoch * 1000).toISOString());

/** Apply a frame to one unit; only newer data moves it (a replay never moves live state back). */
export function mergeUnit<T extends LiveUnit>(u: T, f: LiveFrameUnit): T {
  if (u.ts && new Date(u.ts).getTime() > f.ts * 1000) return u;
  return {
    ...u,
    ts: iso(f.ts),
    age_s: 0,
    lat: f.lat ?? u.lat,
    lon: f.lon ?? u.lon,
    speed: f.speed ?? u.speed,
    heading: f.heading ?? u.heading,
    ignition: f.ignition ?? u.ignition,
    state: f.state || u.state,
    state_since: f.state_since != null ? iso(f.state_since) : u.state_since,
  };
}

/** Merge buffered frames into the list answer; returns the same object when nothing changed. */
export function applyFrames(data: LiveUnitsOut, frames: Map<string, LiveFrameUnit>): LiveUnitsOut {
  let changed = false;
  const units = data.units.map((u) => {
    const f = frames.get(`vehicle:${u.id}`);
    if (!f) return u;
    const next = mergeUnit(u, f);
    if (next !== u) changed = true;
    return next;
  });
  if (!changed) return data;
  const counts: Record<string, number> = {};
  for (const u of units) counts[u.state] = (counts[u.state] ?? 0) + 1;
  return { ...data, units, counts };
}

// ---------------------------------------------------------------- tracks

/** Speed bands of the replay (kept from the old console: 30 / 60 / 90 km/h). */
export function speedTone(kmh: number | null | undefined): GeoTone {
  const v = kmh ?? 0;
  return v < 30 ? 'good' : v < 60 ? 'medium' : v < 90 ? 'caution' : 'bad';
}

export const SPEED_BANDS: { tone: GeoTone; label: string }[] = [
  { tone: 'good', label: '0–30' },
  { tone: 'medium', label: '30–60' },
  { tone: 'caution', label: '60–90' },
  { tone: 'bad', label: '90+' },
];

export interface TrackPoints {
  t: number[];
  coords: LngLat[];
  speed: (number | null)[];
}

/** Positions of a track as [lng, lat] (points without a position dropped), time and speed. */
export function trackPoints(track: { t: number[]; lat: (number | null)[]; lon: (number | null)[]; speed: (number | null)[] } | undefined): TrackPoints {
  const out: TrackPoints = { t: [], coords: [], speed: [] };
  if (!track) return out;
  for (let i = 0; i < track.t.length; i++) {
    const lat = track.lat[i];
    const lon = track.lon[i];
    if (lat == null || lon == null) continue;
    out.t.push(track.t[i]!);
    out.coords.push([lon, lat]);
    out.speed.push(track.speed[i] ?? null);
  }
  return out;
}

/** Split a track into runs of the same speed band (the colour of a piece is its end point's). */
export function speedSegments(p: TrackPoints): GeoLine[] {
  const lines: GeoLine[] = [];
  let current: GeoLine | null = null;
  for (let i = 1; i < p.coords.length; i++) {
    const tone = speedTone(p.speed[i]);
    if (!current || current.tone !== tone) {
      current = { id: `s${i}`, tone, coordinates: [p.coords[i - 1]!] };
      lines.push(current);
    }
    current.coordinates.push(p.coords[i]!);
  }
  return lines;
}

/** Index of the last point at or before `at` (epoch seconds). */
export function indexAt(times: number[], at: number): number {
  let lo = 0;
  let hi = times.length - 1;
  if (hi < 0) return -1;
  if (at <= times[0]!) return 0;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (times[mid]! <= at) lo = mid;
    else hi = mid - 1;
  }
  return lo;
}

// ---------------------------------------------------------------- last values

/** Canonical values worth showing in the focus panel, with their unit (DB-3 names). */
export const VALUE_FIELDS: { key: string; label: string; unit?: string; digits?: number }[] = [
  { key: 'fuel_level', label: msg('Fuel level'), unit: '%', digits: 0 },
  { key: 'ext_voltage', label: msg('Supply voltage'), unit: 'V', digits: 1 },
  { key: 'rpm', label: msg('Engine speed'), unit: 'rpm', digits: 0 },
  { key: 'temp_1', label: msg('Temperature'), unit: '°C', digits: 1 },
  { key: 'soc', label: msg('Battery charge'), unit: '%', digits: 0 },
  { key: 'tacho_state_1', label: msg('Tachograph (driver 1)') },
];
